<script lang="ts">
	import { onMount } from 'svelte';
	import { useCtaServicesApiClient } from '$lib/api/CtaServicesApiClientFactory';
	import type { LogErrorsRequest } from '$lib/api/generated/cta-services';
	import { getCookie } from '$lib/utils/browser-utils';
	import { page } from '$app/stores';
	import { GtmStore } from '$lib/utils/gtm-store';
	import Button from '$lib/components/atoms/Button/Button.svelte';
	import { Section } from '$lib/components/organisms/Section';
	import { browser } from '$app/environment';

	let quick: boolean = false;

	const COOKIE_VERSION_NAME = '24orev';
	const { status, error } = $page;

	if ($page.url.pathname === '/404') {
		quick = true;
	}

	const loadVersion = async (): Promise<string | null> => {
		try {
			const res = await fetch('/git-version.txt');
			if (!res.ok || res.status === 404) {
				return null;
			}
			const serverVersion = await res.text();
			return serverVersion;
		} catch (e) {
			console.log(e);
			return null;
		}
	};

	const dev = process.env.NODE_ENV === 'development';

	const logError = async (status: number, error: any) => {
		try {
			const cookieVersion = getCookie(COOKIE_VERSION_NAME);
			const version = await loadVersion();

			if (status === 500 || status === 502 || status === 503) {
				let userAgent = '';
				if (navigator && navigator.userAgent) {
					userAgent = navigator.userAgent;
				}

				const client = useCtaServicesApiClient();

				const logErrorsRequest: LogErrorsRequest = {
					date: new Date().toISOString(),
					page: $page.url.pathname,
					version: 'SvelteKit StoredV (+error.svelte):' + cookieVersion + ' - ServerV:' + version,
					message: error ? error.message : '',
					stackTrace: error ? ' Browser: ' + userAgent + ' Trace: ' + JSON.stringify(error) : ''
				};
				// don't wait for error logging response
				client.logErrors(logErrorsRequest);
			}
		} catch (e) {
			//Do nothing but we have a problem here!
		}
	};

	/**
	 * Sometimes, we see that the chunk URL has changed and that
	 * results in a 500 error (when we've deployed a new version)
	 * In that case, simply refreshing the page works, so we do that
	 * But we don't want to get stuck in a loop, so we only do it once
	 */
	onMount(async () => {
		/****************************************FORCING RELOAD EVERY 5 mins*********************************************
		 * This is a watchdog for end users. If we broke a deploy we can be almost sure the page is reloading after 10 mins
		 *
		 * ****************************************FORCING RELOAD EVERY 5 mins*******************************************/
		window.setInterval(async () => {
			try {
				if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
					return;
				}
				window.sessionStorage.removeItem('sveltekit-has-refreshed');
				try {
					window.location.reload();
				} catch (e) {}
				try {
					location.reload();
				} catch (e) {}
			} catch (e) {
				//Nothing to do
			}
		}, 300000);
		/****************************************FORCING RELOAD EVERY 5 mins*********************************************/

		setTimeout(
			() => {
				if (typeof window !== 'undefined' && 'sessionStorage' in window && status !== 404) {
					const lastRefreshStr = window.sessionStorage.getItem('sveltekit-has-refreshed');
					const lastRefresh =
						lastRefreshStr && !isNaN(parseInt(lastRefreshStr)) ? parseInt(lastRefreshStr) : 0;
					if (!lastRefresh || lastRefresh === 0 || lastRefresh < Date.now() - 1000 * 30) {
						window.sessionStorage.setItem('sveltekit-has-refreshed', Date.now().toString());
						setTimeout(() => {
							window.location.reload();
						}, 1);
					}
				}
			},
			is500(status) ? 1 : 5000
		);
	});

	const errorTitle = (status: number) => {
		if (status === 404) {
			return error && error.message ? error.message : 'Pagina non trovata';
		}

		if (status === 500 || status === 502 || status === 503) {
			return 'Stiamo aggiornando il sito con i nuovi corsi e master';
		}
	};

	const is500 = (status: number) => {
		return status === 500 || status === 502 || status === 503;
	};

	const errorMessage = (status: number) => {
		if (status === 404) {
			return 'Oops! La pagina che stai cercando non esiste oppure è stata rimossa, clicca il bottone per tornare alla Home Page.';
		}

		if (status === 500 || status === 502 || status === 503) {
			return "Aggiorna la pagina per tornare al sito o attendi qualche istante l'aggiornamento automatico";
		}
	};

	/******************** GTM EVENT PUSH *************************/
	if (browser) {
		GtmStore.pushEvent({
			pageCategory: 'Error'
		});
	}
	/*************************************************************/

	// call API to log the error
	logError(status, error);
</script>

<main>
	<Section>
		<div class="flex flex-col items-center justify-center space-y-6">
			<h1>{status} - {errorTitle(status)}</h1>
			<p class="subtitle text-center">
				{errorMessage(status)}
			</p>

			{#if dev && error}
				<pre>{JSON.stringify(error)}</pre>
			{/if}

			<Button kind="secondary" href="/">Torna alla homepage</Button>
		</div>
	</Section>
</main>
